import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormAlerts, Button, Field } from '@cordata/buffet';
import { Card, CardBody, CardFooter, Row, Col, Alert } from 'reactstrap';
import { Formik, useFormikContext } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { all, spread } from 'axios';
import { format, differenceInYears } from 'date-fns';
import kebabcaseKeys from 'kebabcase-keys';

import { basePyInstance as axios } from '~/utils';

const initialValues = {
  district: '',
  officerName: '',
  childFirstName: undefined,
  childLastName: undefined,
  childGender: undefined,
  childBirthDate: undefined,
  childAge: undefined,
  facility: undefined,
};

const AgeField = () => {
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (values.childBirthDate) {
      setFieldValue('childAge', differenceInYears(new Date(), values.childBirthDate));
    }
  }, [values.childBirthDate]);

  return (
    <Field
      name="childAge"
      step={1}
      type="number"
    />
  );
};

const HWCFields = ({ isValidDistrict, setIsValidDistrict }) => {
  const { values } = useFormikContext();
  const [token, setToken] = useState();
  const [genders, setGenders] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const checkDistrict = () => {
    const t = values.district;
    const headers = {
      Authorization: t,
    };

    setToken(undefined);

    axios.get(
      '/api/handle_with_care/context',
      { headers },
    )
      .then(() => {
        setToken(t);
        setIsValidDistrict(true);
      })
      .catch(() => setIsValidDistrict(false));
  };

  useEffect(() => {
    if (values.district) {
      checkDistrict(values.district);
    }
  }, [values.district]);

  useEffect(() => {
    setGenders([]);
    setFacilities([]);

    if (isValidDistrict && token) {
      const headers = {
        Authorization: token,
      };

      all([
        axios.get(
          '/api/handle_with_care/config/reference-gender',
          {
            headers,
            params: {
              sort: 'default-sort-order,gender-name',
              page: 1,
              'page-size': 10000,
            },
          },
        ),
        axios.get(
          '/api/handle_with_care/public/facility',
          {
            headers,
            params: {
              sort: 'default-sort-order,facility-name',
              page: 1,
              'page-size': 10000,
              'is-in-selections': true,
            },
          },
        ),
      ])
        .then(spread((availableGenders, availableFacilities) => {
          setGenders(availableGenders.data.data);
          setFacilities(availableFacilities.data.data);
        }));
    }
  }, [isValidDistrict, token]);

  return (
    <React.Fragment>
      <Row form>
        <Col md>
          <Field
            name="district"
            label="Agency Identifier"
            required
          />
        </Col>

        <Col md>
          <Field
            name="officerName"
            required
          />
        </Col>
      </Row>

      <hr />

      {isValidDistrict && (
        <React.Fragment>
          <Row form>
            <Col md>
              <Field
                name="childFirstName"
                required
              />
            </Col>

            <Col md>
              <Field
                name="childLastName"
                required
              />
            </Col>

            <Col md>
              <Field
                name="childGender"
                type="select"
                options={genders}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.attributes.genderName}
              />
            </Col>
          </Row>

          <Row form>
            <Col md>
              <Field
                name="childBirthDate"
                type="date"
              />
            </Col>

            <Col md>
              <AgeField
                name="childAge"
                step={1}
                type="number"
              />
            </Col>
          </Row>

          <Field
            name="facility"
            label="School"
            type="select"
            options={facilities}
            getOptionValue={o => o.id}
            getOptionLabel={o => o.attributes && o.attributes.facilityName}
            creatable
            getNewOptionData={(inputValue, optionLabel) => ({
              isNew: true,
              id: optionLabel,
              attributes: { facilityName: optionLabel },
            })}
            required
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

HWCFields.propTypes = {
  isValidDistrict: PropTypes.bool.isRequired,
  setIsValidDistrict: PropTypes.func.isRequired,
};

const HWCForm = () => {
  const [isValidDistrict, setIsValidDistrict] = useState(false);

  return (
    <Formik
      displayName="HandleWithCare"
      validateOnBlur={false}
      validateOnChange={false}

      initialValues={initialValues}
      initalStatus={{ success: false }}

      validationSchema={Yup.object().shape({
        district: Yup.string().required(),
        officerName: Yup.string().required(),
        childFirstName: Yup.string().required(),
        childLastName: Yup.string().required(),
        childAge: Yup.number().integer().positive(),
        facility: Yup.mixed().required(),
      })}

      onSubmit={(values, {
        resetForm, setStatus, setErrors, setSubmitting,
      }) => {
        const p = {
          ...values,
          childBirthDate: values.childBirthDate && format(values.childBirthDate, 'YYYY-MM-DD'),
          childGenderUid: values.childGender && values.childGender.id,
          facilityUid: values.facility.isNew ? undefined : values.facility.id,
          facilityName: values.facility.attributes.facilityName,
          childAge: values.childAge && parseInt(values.childAge, 10),
        };

        delete p.district;
        delete p.facility;
        delete p.childGender;

        axios
          .post(
            '/api/handle_with_care/submit',
            kebabcaseKeys(p, { deep: true }),
            { headers: { Authorization: values.district } },
          )
          .then(() => {
            setStatus({ success: true });
            setIsValidDistrict(false);

            setTimeout(() => {
              resetForm();
            }, 2500);
          })
          .catch((e) => {
            const errs = e.response.data ? e.response.data.errors : e;

            setErrors({ server: e });

            if (errs && Object.keys(errs).some(er => Object.keys(values).includes(er))) {
              Object.keys(errs).forEach(er => setErrors({ [er]: errs[er] !== 'Required' ? errs[er] : 'is required' }));
            }
          })
          .then(() => setSubmitting(false));
      }}
    >
      {({
        values,
        errors,
        status,
        handleSubmit,
        handleReset,
        submitCount,
        isSubmitting,
      }) => (
        <Card>
          <form onSubmit={handleSubmit} noValidate>
            <CardBody>
              {submitCount >= 1 && errors && (
                <FormAlerts errors={errors} />
              )}

              {status && status.success && (<Alert color="success">Form Sent!</Alert>)}

              <HWCFields
                isValidDistrict={isValidDistrict}
                setIsValidDistrict={setIsValidDistrict}
              />
            </CardBody>

            <CardFooter className="d-flex">
              <div className="mr-auto">
                <Button
                  color="danger"
                  type="button"
                  outline
                  disabled={isSubmitting}
                  onClick={() => {
                    handleReset(initialValues);
                    setIsValidDistrict(false);
                  }}
                >
                  <FontAwesomeIcon icon="sync-alt" />
                  <span className="d-none d-md-inline ml-2">Reset</span>
                </Button>
              </div>

              <div className="ml-auto">
                <Button
                  type="submit"
                  disabled={!isValidDistrict || !values.district.length || isSubmitting}
                >
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </form>
        </Card>
      )}
    </Formik>
  );
};

export default HWCForm;
